import { call, put, select } from 'redux-saga/effects';
import { actionActions } from 'store/data/action';
import { improvementAreaSelectors } from 'store/data/improvementArea';
import { sagaWrapper } from 'store/helpers';
import { ActionType } from 'typesafe-actions';
import { timeUtility } from 'utility';

import * as improvementsActions from './actions';
import * as constants from './constants';
import * as improvementsSelectors from './selectors';

// Inserts a temporary placeholder action so the user can fill in the form
// before it is persisted. Replaces any existing mock to avoid duplicates.
//
// improvementAreaId resolution:
//   1. If the Actions tab's filter dropdown holds a real area id, use it —
//      the dropdown selection doubles as the create default for that tab.
//   2. If it is `null` (All) or `UNASSIGNED_AREA_ID`, fall back to the
//      lowest-sortOrder area. The API requires non-null on create, so an
//      empty tenant still surfaces a validation error in the detail editor.
export function* startCreateMockActionSaga(
  action: ActionType<typeof improvementsActions.startCreateMockAction>,
) {
  function* saga() {
    const startAt = timeUtility.getToday();
    const endAt = timeUtility.step({
      timeDate: startAt,
      step: 'next',
      stepSize: 'week',
    });

    const selected: ReturnType<typeof improvementsSelectors.actionsAreaId> =
      yield select(improvementsSelectors.actionsAreaId);
    const defaultImprovementAreaId: ReturnType<
      typeof improvementAreaSelectors.defaultImprovementAreaId
    > = yield select(improvementAreaSelectors.defaultImprovementAreaId);

    const improvementAreaId =
      selected === null || selected === constants.UNASSIGNED_AREA_ID
        ? defaultImprovementAreaId
        : selected;

    const mockAction: Data.Action = {
      id: constants.MOCKED_ACTION_ID,
      improvementAreaId,
      name: '',
      problemStatement: '',
      solutionProposal: '',
      status: 'New',
      definitionOfDone: '',
      startAt,
      endAt,
      priority: 'None',
      responsibleEmployee: '',
      suggestionIds: [],
    };

    const hasMockedAction: ReturnType<
      typeof improvementsSelectors.hasMockedAction
    > = yield select(improvementsSelectors.hasMockedAction);

    // Remove stale mock before adding the fresh one
    if (hasMockedAction) {
      yield put(actionActions.remove(mockAction.id));
    }

    yield put(actionActions.add(mockAction));
    yield put(improvementsActions.setSelectedActionId(mockAction.id));
  }

  yield call(sagaWrapper, { saga, type: action.type });
}
